/**
 * Session Manager - Handles session lifecycle, customer association, and analytics integration
 * Integrates with existing analytics tracking and the new real-time logging system
 */

import { initializeFrontendLogCapture } from './frontendLogCapture.js';

class SessionManager {
    constructor() {
        this.sessionId = null;
        this.customerId = null;
        this.customerData = null;
        this.isActive = false;
        this.startTime = null;
        this.logCapture = null;
        this.heartbeatInterval = null;

        this.initialize();
    }

    initialize() {
        // Initialize session
        this.startSession();

        // Initialize frontend log capture
        this.logCapture = initializeFrontendLogCapture();

        // Setup page tracking
        this.setupPageTracking();

        // Setup heartbeat
        this.startHeartbeat();

        // Setup beforeunload handler
        this.setupUnloadHandler();

        console.log(`🎯 Session Manager initialized for session: ${this.sessionId}`);
    }

    startSession() {
        // Get or create session ID
        this.sessionId = this.getOrCreateSessionId();
        this.startTime = new Date();
        this.isActive = true;

        // Notify backend about session start
        this.notifySessionStart();

        // Track page view
        this.trackPageView();

        // Make session globally available
        window.currentSessionId = this.sessionId;
        window.sessionManager = this;
    }

    getOrCreateSessionId() {
        // Try multiple sources for existing session
        let sessionId =
            sessionStorage.getItem('sessionId') ||
            this.generateSessionId(); // Removed localStorage check for more unique sessions

        // Store it in sessionStorage only (more ephemeral)
        sessionStorage.setItem('sessionId', sessionId);

        return sessionId;
    }

    generateSessionId() {
        const timestamp = Date.now();
        const randomPart = Math.random().toString(36).substr(2, 12);
        const extraRandom = Math.random().toString(36).substr(2, 6);
        return `session_${timestamp}_${randomPart}${extraRandom}`;
    }

    async notifySessionStart() {
        try {
            const payload = {
                sessionId: this.sessionId,
                userAgent: navigator.userAgent,
                initialUrl: window.location.href,
                timestamp: new Date().toISOString(),
                viewport: {
                    width: window.innerWidth,
                    height: window.innerHeight
                },
                referrer: document.referrer
            };

            // Call both the new logs API and existing analytics API
            await Promise.all([
                // New real-time logging system
                fetch('/api/logs/session/start', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Session-ID': this.sessionId
                    },
                    body: JSON.stringify(payload)
                }),

                // Existing analytics system (for backward compatibility)
                this.trackAnalytics('session_start', window.location.pathname, payload)
            ]);

            console.log(`📊 Session start notified: ${this.sessionId}`);
        } catch (error) {
            console.error('Failed to notify session start:', error);
        }
    }

    async linkCustomer(customerData) {
        if (!customerData || !customerData.id) {
            console.error('Invalid customer data provided');
            return;
        }

        this.customerId = customerData.id;
        this.customerData = customerData;

        // Update session storage
        sessionStorage.setItem('customerId', customerData.id);
        sessionStorage.setItem('customerData', JSON.stringify(customerData));

        // Update log capture with customer info
        if (this.logCapture) {
            this.logCapture.setCustomerId(customerData.id, customerData);
        }

        try {
            const payload = {
                customerId: customerData.id,
                customerEmail: customerData.email,
                customerName: `${customerData.firstName || customerData.first_name || ''} ${customerData.lastName || customerData.last_name || ''}`.trim()
            };

            // Notify both systems
            await Promise.all([
                // New real-time logging system
                fetch(`/api/logs/session/${this.sessionId}/link-customer`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Session-ID': this.sessionId,
                        'X-Customer-ID': customerData.id
                    },
                    body: JSON.stringify(payload)
                }),

                // Existing analytics system - update all activities in this session
                fetch(`/api/sessions/${this.sessionId}/link-customer`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        customerId: customerData.id
                    })
                })
            ]);

            // Track the customer association event
            this.trackAnalytics('customer_linked', window.location.pathname, {
                customerId: customerData.id,
                customerEmail: customerData.email,
                customerName: payload.customerName,
                linkedAt: new Date().toISOString()
            });

            console.log(`🔗 Customer linked to session: ${customerData.id}`);
        } catch (error) {
            console.error('Failed to link customer to session:', error);
        }
    }

    trackUserAction(action, data = {}) {
        const actionData = {
            action,
            page: window.location.pathname,
            timestamp: new Date().toISOString(),
            data
        };

        // Use log capture for new system
        if (this.logCapture) {
            this.logCapture.logUserAction(action, window.location.pathname, data);
        }

        // Use existing analytics for backward compatibility
        this.trackAnalytics(action, window.location.pathname, actionData);
    }

    trackPageView(page = window.location.pathname) {
        const pageData = {
            page,
            referrer: document.referrer,
            timestamp: new Date().toISOString(),
            title: document.title
        };

        // Use log capture for new system
        if (this.logCapture) {
            this.logCapture.logPageView(page);
        }

        // Use existing analytics for backward compatibility
        this.trackAnalytics('page_view', page, pageData);
    }

    trackError(error, context = {}) {
        const errorData = {
            message: error.message || error,
            stack: error.stack,
            page: window.location.pathname,
            timestamp: new Date().toISOString(),
            context
        };

        // Use log capture for new system
        if (this.logCapture) {
            this.logCapture.logError(error, context);
        }

        // Use existing analytics for backward compatibility
        this.trackAnalytics('error', window.location.pathname, errorData);
    }

    async trackAnalytics(action, page, data = {}) {
        // Don't track analytics if no valid session exists
        if (!this.sessionId) {
            console.warn('📊 SessionManager: Skipping analytics due to missing sessionId', { action, page });
            return;
        }

        try {
            await fetch('/api/analytics', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-ID': this.sessionId,
                    ...(this.customerId && { 'X-Customer-ID': this.customerId })
                },
                body: JSON.stringify({
                    sessionId: this.sessionId,
                    action,
                    page,
                    data,
                    timestamp: new Date().toISOString()
                })
            });
        } catch (error) {
            console.error('Failed to track analytics:', error);
        }
    }

    setupPageTracking() {
        // Track initial page load
        this.trackPageView();

        // Track page changes (for SPAs)
        let currentPath = window.location.pathname;

        // Observe URL changes
        const observer = new MutationObserver(() => {
            if (window.location.pathname !== currentPath) {
                currentPath = window.location.pathname;
                this.trackPageView(currentPath);
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Also listen for popstate events (back/forward navigation)
        window.addEventListener('popstate', () => {
            this.trackPageView(window.location.pathname);
        });
    }

    startHeartbeat() {
        // Send periodic heartbeat to keep session alive and track activity
        this.heartbeatInterval = setInterval(() => {
            if (this.isActive) {
                this.trackAnalytics('heartbeat', window.location.pathname, {
                    timestamp: new Date().toISOString(),
                    visible: !document.hidden
                });
            }
        }, 30000); // Every 30 seconds
    }

    setupUnloadHandler() {
        // Track session end when user leaves
        window.addEventListener('beforeunload', () => {
            this.endSession();
        });

        // Also track when page becomes hidden
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.trackAnalytics('page_hidden', window.location.pathname, {
                    timestamp: new Date().toISOString()
                });
            } else {
                this.trackAnalytics('page_visible', window.location.pathname, {
                    timestamp: new Date().toISOString()
                });
            }
        });
    }

    endSession() {
        if (!this.isActive) return;

        this.isActive = false;

        // Clear heartbeat
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }

        // Track session end
        const endData = {
            sessionId: this.sessionId,
            duration: Date.now() - this.startTime.getTime(),
            endTime: new Date().toISOString(),
            customerId: this.customerId
        };

        // Use sendBeacon for reliable delivery during page unload
        if (navigator.sendBeacon) {
            navigator.sendBeacon('/api/analytics', JSON.stringify({
                sessionId: this.sessionId,
                action: 'session_end',
                page: window.location.pathname,
                data: endData,
                timestamp: new Date().toISOString()
            }));
        }

        console.log(`📊 Session ended: ${this.sessionId}`);
    }

    // Public API methods
    getSessionId() {
        return this.sessionId;
    }

    getCustomerId() {
        return this.customerId;
    }

    getCustomerData() {
        return this.customerData;
    }

    isCustomerLinked() {
        return !!this.customerId;
    }

    getSessionDuration() {
        if (!this.startTime) return 0;
        return Date.now() - this.startTime.getTime();
    }

    getSessionInfo() {
        return {
            sessionId: this.sessionId,
            customerId: this.customerId,
            customerData: this.customerData,
            isActive: this.isActive,
            startTime: this.startTime,
            duration: this.getSessionDuration(),
            isCustomerLinked: this.isCustomerLinked()
        };
    }
}

// Create and initialize session manager
let sessionManager = null;

export function initializeSessionManager() {
    if (sessionManager) {
        console.warn('Session manager already initialized');
        return sessionManager;
    }

    sessionManager = new SessionManager();
    return sessionManager;
}

export function getSessionManager() {
    return sessionManager;
}

// Auto-initialize if not in test environment
if (typeof window !== 'undefined' && !window.location.href.includes('test')) {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeSessionManager);
    } else {
        initializeSessionManager();
    }
}

export default SessionManager;